import { useEffect, useState } from 'react'
import api from '../../api/api'
import { useTranslation } from 'react-i18next'
import { useProfileStore } from '../../store/useUserStore'
import { getCurrentDate } from '../../helpers/helper'
import { RiRefreshFill } from 'react-icons/ri'
import { LiaCalendarTimes } from 'react-icons/lia'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'

const ScheduleList = () => {
    const [schedules, setSchedules] = useState([])
    const [selectedDate, setSelectedDate] = useState<Date>(new Date())
    const [monthDates, setMonthDates] = useState<string[]>([]) // Dates with courses for calendar highlighting
    const { t } = useTranslation()
    const {setLoading} = useProfileStore((state) => state)

    const fetchSchedules = (date: Date) => {
        setLoading(true)
        // Use local date to avoid timezone offset issues
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const dateStr = `${year}-${month}-${day}`
        
        // Fetch week schedule for display
        api.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/${dateStr}?mode=week`).then(res => {
            // console.log("schedule", res.data.data)
            setSchedules(res.data.data)
        }).catch(err => {
            console.log("err cart", err)
        }).finally(() => {        
            setLoading(false)
        })

        // Fetch month schedule for calendar highlighting
        api.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/${dateStr}?mode=month`).then(res => {
            const dates = (res.data.data || [])
                .filter((s: any) => s.courses && s.courses.length > 0)
                .map((s: any) => s.date)
            setMonthDates(dates)
        }).catch(err => {
            console.log("err month schedule", err)
        })
    }

    useEffect(() => {
        fetchSchedules(selectedDate)
    }, [])

    const handleDateClick = (date: Date) => {
        setSelectedDate(date)
        fetchSchedules(date)
    }

    
    const getDayOfWeek = (dateString: string): string => {
        const daysOfWeek = [t("sunday"), t("monday"), t("tuesday"), t("wednesday"), t("thursday"), t("friday"), t("saturday")]
        const date = new Date(dateString)
        return daysOfWeek[date.getDay()]
    }

    // Build a set of dates that have courses for calendar highlighting (from month API)
    const scheduleDateSet = new Set(monthDates)

    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            // Use local date to avoid timezone offset issues
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const dateStr = `${year}-${month}-${day}`
            if (scheduleDateSet.has(dateStr)) {
                return 'bg-theme text-white rounded-lg'
            }
        }
        return null
    }
    
    return (
        <div className="pb-28">
            <div className='mb-2 px-3'>
                <div className='text-lg font-medium'>{t("schedule.upcoming_class")}</div>
            </div>

            {/* Calendar */}
            <div className="px-3 mb-4">
                <div className="flex justify-center">
                    <Calendar
                        value={selectedDate}
                        onClickDay={handleDateClick}
                        tileClassName={tileClassName as any}
                        className="mx-auto"
                        next2Label={null}
                        prev2Label={null}
                        minDetail="year"
                        showNeighboringMonth={false}
                    />
                </div>
            </div>

            <div className="px-3">
                {schedules.map((schedule:any) => (
                    <div className='border-b-2 mb-2'>
                        <div className='text-lg font-semibold'>{getDayOfWeek(schedule.date)}, {schedule.date}</div>
                        {schedule.courses.length > 0 ? schedule.courses.map((course:any) => (
                        <div className='mb-3'>
                            <div className='grid grid-cols-6 gap-2 pt-2.5 items-center rounded-3xl'>
                                <div className='col-span-1'>
                                    {course.images.length > 0 ? (
                                        <img src={course.images[0]} alt="" className='w-10 h-10 rounded-3xl'/>
                                    ):(
                                        <img src={ "/assets/img/products/product1.png"} alt="" className='w-10 rounded-3xl'/>
                                    )}
                                </div>
                                <div className='col-span-3'>
                                    <div className='text-lg font-semibold truncate'>{course.name || course.course_name || "-"}</div>
                                    <div className='text-md font-light truncate'>{course.student_name}</div>
                                </div>
                                <div className='col-span-2 text-md font-light text-end self-start'>
                                    <div className='truncate'>{course.branch_name}</div>
                                    <div>{course.start_time} - {course.end_time}</div>
                                    {course.status === "canceled" && (<div className='bg-red-500 rounded-2xl text-white text-center ml-auto mt-1 truncate'>{t("schedule.canceled")}</div>)}
                                    {(course.status === "replacement_class" || course.status == "replacement") && (<div className='bg-blue-600 rounded-2xl text-white text-center ml-auto mt-1 truncate'>{t("schedule.replacement")}</div>)}
                                    {course.status === "replaced" && (<div className='bg-gray-500 rounded-2xl text-white text-center ml-auto mt-1 truncate'>{t("schedule.replaced")}</div>)}
                                </div>
                            </div>
                            {/* Remark for canceled */}
                            {course.status === "canceled" && course.remark && (
                                <div className="flex items-center border border-red-300 rounded-xl px-4 py-2 mt-2 text-red-600 text-lg font-medium gap-2">
                                    <LiaCalendarTimes color="red" size={30} />
                                    {course.remark}
                                </div>
                            )}
                            {/* Remark for replacement */}
                            {(course.status === "replacement_class" || course.status === "replacement") && course.remark && (
                                <div className="flex items-center border border-blue-400 rounded-xl px-4 py-2 mt-2 text-blue-700 text-lg font-medium gap-2">
                                    <RiRefreshFill color="blue" size={30} />
                                    {course.remark}
                                </div>
                            )}
                        </div>
                        )) : (
                            <div className='text-center border rounded-3xl mb-3 mt-2 py-3'>
                                {t("schedule.no_class_today")}
                            </div>
                        )}
                    </div>

                ))}
            </div>
        </div>
    )
}

export default ScheduleList